import { AlertFactory, AlertState } from 'alerts/Alert'
import ModalViewModel from 'infra/ModalViewModel'
import {
  action,
  computed,
  intercept,
  makeObservable,
  observable,
  override,
  runInAction,
} from 'mobx'
import { task } from 'mobx-task'
import { validationContext, func } from 'validx'
import Workspace, { WorkspaceClassification } from 'iam/models/Workspace'
import { Membership } from 'iam/WorkspaceMembershipStore'
import { Id } from 'types/definitions'
import MemberStore from 'iam/MemberStore'
import { CustomerStore } from 'iam/CustomerStore'
import Member from 'iam/models/Member'
import { Portal as DtoPortal, UserSource } from 'iam/dtos/member'

export class CallCenterAddClientDialogViewModel extends ModalViewModel {
  @observable
  email = ''

  @observable
  firstName = ''

  @observable
  lastName = ''

  @observable
  phone = ''

  @observable
  selectedWorkspace: Workspace | null = null

  readonly validation = validationContext(this, {
    email: [
      func(
        ({ value }) => typeof value === 'string' && value.trim().length > 0,
        'Email is required.',
      ),
    ],
    firstName: [
      func(
        ({ value }) => typeof value === 'string' && value.trim().length > 0,
        'First name is required.',
      ),
    ],
    lastName: [
      func(
        ({ value }) => typeof value === 'string' && value.trim().length > 0,
        'Last name is required.',
      ),
    ],
    phone: [
      func(
        ({ value }) => typeof value === 'string' && value.trim().length > 0,
        'Phone number is required.',
      ),
    ],
    selectedWorkspace: [
      func(() => this.selectedWorkspace !== null, 'Workspace is required.'),
    ],
  })

  constructor(
    private memberStore: MemberStore,
    private customerStore: CustomerStore,
    private getAvailableMemberships: () => Membership[],
    private onClientCreated: (membership: Membership) => void,
    private newAlert: AlertFactory,
  ) {
    super()
    makeObservable(this)
    intercept(this, (change) => {
      this.validation.clearErrors(String(change.name))
      return change
    })
  }

  @override
  show() {
    this.validation.reset()
    super.show()

    if (!this.selectedWorkspace && this.availableWorkspaces.length === 1) {
      this.setWorkspace(this.availableWorkspaces[0] ?? null)
    }
  }

  @override
  close() {
    this.reset()
    this.validation.reset()
    super.close()
  }

  @computed
  get availableWorkspaces() {
    return this.getAvailableMemberships().map(
      (membership) => membership.workspace,
    )
  }

  @computed
  get isMagicLinkWorkspace(): boolean {
    return Boolean(
      this.selectedWorkspace?.config?.external_customers_config?.enabled,
    )
  }

  @action.bound
  setWorkspace(workspace: Workspace | null) {
    this.selectedWorkspace = workspace
  }

  @action.bound
  prefill({
    email,
    firstName,
    lastName,
    phone,
    workspaceId,
    workspace,
  }: {
    email?: string
    firstName?: string
    lastName?: string
    phone?: string
    workspaceId?: string
    workspace?: Workspace | null
  }) {
    this.email = email?.trim() ?? ''
    this.firstName = firstName?.trim() ?? ''
    this.lastName = lastName?.trim() ?? ''
    this.phone = phone?.trim() ?? ''

    if (workspaceId) {
      const availableWorkspace =
        this.availableWorkspaces.find((item) => item.id === workspaceId) ||
        (workspace && workspace.id === workspaceId ? workspace : null)

      if (availableWorkspace) {
        this.setWorkspace(availableWorkspace)
      } else {
        this.selectedWorkspace = null
      }
    }
  }

  @action.bound
  reset() {
    this.email = ''
    this.firstName = ''
    this.lastName = ''
    this.phone = ''
    this.selectedWorkspace = null
  }

  submit = task.resolved(async () => {
    if (!this.validation.reset().validate().isValid) {
      return
    }

    const workspace = this.selectedWorkspace

    if (!workspace) {
      return
    }

    const email = this.email.trim()
    const firstName = this.firstName.trim()
    const lastName = this.lastName.trim()
    const phone = this.phone.trim()

    let createdMember: Member | null = null

    try {
      if (this.isMagicLinkWorkspace) {
        const response = await this.customerStore.create({
          workspace_id: Id.assert(workspace.id),
          email,
          given_name: firstName,
          family_name: lastName,
          phone_number: phone || undefined,
        })

        createdMember = await this.memberStore.fetchByUserPublicId({
          workspace_id: Id.assert(workspace.id),
          user_public_id: response.id,
        })
      } else {
        createdMember = await this.memberStore.createManually({
          workspace_id: Id.assert(workspace.id),
          email,
          given_name: firstName || undefined,
          family_name: lastName || undefined,
          phone: phone || undefined,
          source: UserSource.Local,
          portal: DtoPortal.CustomerPortal,
        })
      }
    } catch (error) {
      this.newAlert()
        .withState(AlertState.Error)
        .saying('Unable to create client', 'Please try again')
        .autoDismiss()
        .show()
      return
    }

    this.newAlert()
      .withState(AlertState.Success)
      .saying('Client created')
      .autoDismiss()
      .show()

    this.close()

    if (createdMember) {
      this.onClientCreated({ workspace, member: createdMember })
    }
  })
}
